import clsx from 'clsx';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { type AnalyticsContextSpaceContent, logEvent } from '@gitbook/app-analytics/client';
import * as hiveCore from '@gitbook/hive-core';
import { hasPageCover } from '@gitbook/hive-core';
import { useHiveBranch, useHiveComputedOrigin } from '@gitbook/hive-react';
import { ResponsiveButtonsRow, type ResponsiveRowButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { InlineTextareaEdit, LoadingIndicator } from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';
import { assertIsDefined } from '@gitbook/util-ts';

import {
    CommentButton,
    PageIconEditable,
    type PageIconEditableRef,
    TextDiff,
    useComments,
    usePageLayoutOptions,
} from '../../../../Components';
import { useNavigation, useNavigationState } from '../../../../navigation';
import { useAdaptiveContextForSpace } from '../../../Site';
import { DiffContext } from '../../Diff';
import type { PageToolbarProps } from '../PageToolbar';
import { PageComputedHeaderAlert } from './PageComputedHeaderAlert';
import { PageImportHeaderAlert } from './PageImportHeaderAlert';

/**
 * Header for a page.
 */
export const PageHeader = React.memo(function PageHeaderMemo(props: {
    space: api.Space;
    page: hiveCore.RevisionPageDocument;
    readOnly: boolean;
    onSubmitDescription: () => void;
    analyticsContext: AnalyticsContextSpaceContent;
    withDiff?: boolean;
    toolbar?: React.FC<PageToolbarProps>;
    pageTitleRef?: React.MutableRefObject<HTMLTextAreaElement | null>;
}): React.ReactElement {
    const {
        space,
        page,
        readOnly,
        onSubmitDescription,
        analyticsContext,
        withDiff,
        toolbar: PageToolbar,
        pageTitleRef,
    } = props;
    const [loading, wrapLoading] = useLoadingState();

    const hiveBranch = useHiveBranch();

    const descriptionRef = React.useRef<HTMLTextAreaElement>(null);
    const editableRef = React.useRef<PageIconEditableRef>(null);

    const pageOptions = usePageLayoutOptions(page);

    // When the page's title is modified
    const onChangeTitle = async (newTitle: string) => {
        assertIsDefined(hiveBranch);

        await hiveBranch.change((change) => {
            change.renamePage(page.id, newTitle);
        });

        logEvent({
            ...analyticsContext,
            type: 'edit_page_title',
            pageKind: page.type,
        });
    };

    // When page's description is modified
    const onChangeDescription = async (newDescription: string) => {
        assertIsDefined(hiveBranch);

        await hiveBranch.change((change) => {
            change.setPageDescription(page.id, newDescription);
        });

        logEvent({
            ...analyticsContext,
            type: 'edit_page_description',
            pageKind: page.type,
        });
    };

    const onAddCover = () => {
        wrapLoading(async () => {
            assertIsDefined(hiveBranch);

            await hiveBranch.change((change) => {
                change.addPageCover(page.id);
            });

            logEvent({
                ...analyticsContext,
                type: 'edit_add_cover',
            });
        });
    };

    const onAddIcon = () => {
        editableRef.current?.open();
    };

    const shouldHideTitle = !pageOptions.title;
    const shouldHideDescription = !pageOptions.description;
    const computedOrigin = useHiveComputedOrigin({
        objectType: 'pages',
        objectId: page.id,
    });

    return (
        <div
            data-testid="page.header"
            className={clsx(
                'group/page-header relative flex max-w-[--block-wrapper-max-width] page-block-openapi:max-w-full flex-1 flex-col',
                computedOrigin ? 'pt-4' : 'pt-2',
                readOnly && shouldHideTitle && shouldHideDescription ? null : 'mb-6'
            )}
        >
            <PageImportHeaderAlert pageId={page.id} />
            {computedOrigin ? (
                <PageComputedHeaderAlert computedOrigin={computedOrigin} />
            ) : (
                <PageHeaderActions
                    readOnly={readOnly}
                    page={page}
                    onAddIcon={onAddIcon}
                    onAddCover={onAddCover}
                    loading={loading}
                />
            )}

            <div className="flex justify-between">
                <div className="flex flex-1 flex-col">
                    {readOnly ? null : <PageHeaderHiddenItems page={page} />}
                    <div className="flex flex-col gap-4">
                        {/* Title and emoji picker */}
                        {shouldHideTitle ? null : (
                            <div className="flex items-start justify-between">
                                <div className="flex h-full flex-1 self-stretch">
                                    {/* Emoji */}
                                    <div className="flex h-10 w-auto shrink-0 flex-row items-center justify-end lg:h-12">
                                        {page.emoji || page.icon || !readOnly ? (
                                            <div
                                                className="relative flex pr-2 pl-0 lg:absolute lg:pr-2 lg:pl-4"
                                                data-nudge-icon
                                            >
                                                <PageIconEditable
                                                    ref={editableRef}
                                                    analyticsContext={analyticsContext}
                                                    page={page}
                                                    readOnly={readOnly}
                                                    size={24}
                                                />
                                            </div>
                                        ) : null}
                                    </div>

                                    {/* Title */}
                                    {readOnly ? (
                                        <div className="flex">
                                            <h1
                                                data-testid="page.titleReadOnly"
                                                className="hyphens-auto break-words text-content-page-title-medium lg:text-content-page-title-large"
                                            >
                                                {withDiff ? (
                                                    <PagePropDiff page={page} prop="title" />
                                                ) : (
                                                    page.title
                                                )}
                                            </h1>
                                        </div>
                                    ) : (
                                        <InlineTextareaEdit
                                            data-testid="page.titleInput"
                                            ref={pageTitleRef}
                                            key={page.id}
                                            value={page.title}
                                            onSave={onChangeTitle}
                                            placeholder="Untitled page"
                                            maxLength={hiveCore.PAGE_TITLE_LIMIT}
                                            className="size-full text-content-page-title-medium lg:text-content-page-title-large"
                                            textareaClassName="size-full text-content-page-title-medium lg:text-content-page-title-large"
                                            transformValue={preventNewLines}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter') {
                                                    descriptionRef.current?.focus();
                                                }
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                        {/* Page description */}
                        {shouldHideDescription ? null : (
                            <div className={clsx('flex items-center justify-between')}>
                                <div className={clsx('flex flex-1')}>
                                    {readOnly ? (
                                        page.description || withDiff ? (
                                            <div className="text-content-paragraph text-muted">
                                                {withDiff ? (
                                                    <PagePropDiff page={page} prop="description" />
                                                ) : (
                                                    page.description
                                                )}
                                            </div>
                                        ) : // This view prevents the content from shifting when switching between modes and  the description is absent
                                        withDiff ? (
                                            <div className="h-6 max-md:hidden" />
                                        ) : null
                                    ) : (
                                        <InlineTextareaEdit
                                            data-testid="page.descriptionInput"
                                            ref={descriptionRef}
                                            textareaClassName="w-full text-content-paragraph text-muted"
                                            key={page.id}
                                            value={page.description || ''}
                                            onSave={onChangeDescription}
                                            placeholder="Page description (optional)"
                                            maxLength={hiveCore.PAGE_DESCRIPTION_LIMIT}
                                            className="w-full text-content-paragraph text-muted"
                                            transformValue={preventNewLines}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter') {
                                                    onSubmitDescription();
                                                }
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Page options button */}
                {PageToolbar ? (
                    <PageOptionsToolbarButton
                        space={space}
                        toolbar={PageToolbar}
                        page={page}
                        readOnly={readOnly}
                        analyticsContext={analyticsContext}
                    />
                ) : null}
            </div>
        </div>
    );
});

function PagePropDiff(props: {
    page: hiveCore.RevisionPageDocument;
    prop: 'title' | 'description';
}) {
    const { page, prop } = props;
    const { baseRevision } = React.useContext(DiffContext);

    const previousPage = baseRevision?.pages[page.id];
    const previousTitle = previousPage?.[prop] ?? '';
    const currentTitle = page[prop] ?? '';

    return <TextDiff before={previousTitle} after={currentTitle} />;
}

function PageHeaderHiddenItems(props: { page: hiveCore.RevisionPageDocument }) {
    const { page } = props;
    const pageOptions = usePageLayoutOptions(page);
    const { title, description } = pageOptions;
    return title && description ? null : (
        <div className="mt-2 flex items-center gap-2">
            {!title ? <PageHeaderHiddenItem>Page title hidden</PageHeaderHiddenItem> : null}
            {!description ? (
                <PageHeaderHiddenItem>Page description hidden</PageHeaderHiddenItem>
            ) : null}
        </div>
    );
}

function PageHeaderHiddenItem(props: { children: React.ReactNode; className?: string }) {
    return (
        <div
            className={clsx(
                'flex items-center gap-1 opacity-0 transition group-hover/page-header:opacity-100',
                props.className
            )}
        >
            <Icon.EyeOff size={16} />
            <div className="text-muted text-ui-small">{props.children}</div>
        </div>
    );
}

function PageOptionsToolbarButton(props: {
    space: api.Space;
    page: hiveCore.RevisionPageDocument;
    readOnly: boolean;
    toolbar: React.FC<PageToolbarProps>;
    analyticsContext: AnalyticsContextSpaceContent;
}) {
    const { space, page, readOnly, analyticsContext, toolbar: PageToolbar } = props;
    const adaptiveContentContext = useAdaptiveContextForSpace(space);

    return (
        <PageToolbar
            space={space}
            page={page}
            readOnly={readOnly}
            adaptiveContentContext={adaptiveContentContext}
            analyticsContext={analyticsContext}
            className="mt-1.5"
        />
    );
}

function preventNewLines(value: string) {
    return value.replace(/\n/g, ' ');
}

function PageHeaderActions(props: {
    readOnly: boolean;
    page: hiveCore.RevisionPageDocument;
    onAddIcon: () => void;
    onAddCover: () => void;
    loading: boolean;
}) {
    const { readOnly, page, onAddIcon, onAddCover, loading } = props;
    const { navigate } = useNavigation();
    const { screen } = useNavigationState();
    const { hasComments } = useComments({
        nodeKey: undefined,
        tableCell: undefined,
    });

    const commentButton: ResponsiveRowButton = {
        key: 'comment-on-page',
        canOverflow: false,
        label: 'Comment on page',
        onPress: () => {},
        wrap: () => (
            <CommentButton nodeKey={undefined} label="Comment on page" kind="blank" size="small" />
        ),
    };

    const buttons = (() => {
        const options: ResponsiveRowButton[] = [];

        if (!readOnly) {
            options.push({
                key: 'page-options',
                label: 'Page options',
                leadingIcon: Icon.Options02,
                onPress: () => {
                    navigate({ screen: { sideSheet: { type: 'page' } } });
                },
            });

            if (!(page.icon || page.emoji)) {
                options.push({
                    key: 'add-icon',
                    label: 'Add icon',
                    leadingIcon: Icon.EmojiReaction,
                    onPress: onAddIcon,
                    disabled: loading,
                });
            }

            if (!hasPageCover(page)) {
                options.push({
                    key: 'add-cover',
                    label: 'Add cover',
                    leadingIcon: loading ? LoadingIndicator : Icon.Image,
                    onPress: onAddCover,
                    disabled: loading,
                });
            }
        }

        if (screen.type === 'space' && !screen.revision) {
            options.push(commentButton);
        }

        return options;
    })();

    return (
        <div
            className={clsx(
                "flex min-h-6 gap-x-1 transition group-hover/page-header:opacity-100 has-[button[aria-expanded='true']]:opacity-100",
                hasComments ? 'opacity-100' : 'opacity-0'
            )}
        >
            <ResponsiveButtonsRow kind="blank" size="small" buttons={buttons} />
        </div>
    );
}
